import { useTranslation } from 'react-i18next';
import { LinkModal } from '../../../../components/ATS/LinkModal/LinkModal';
import { useDontShowAgain } from '../../../../components/ATS/LinkModal/use-dont-show-again';
import { Link } from '../../../../components/Link';
import { Thumbnail } from '../../../../components/thumbnail/thumbnail';
import { ScriptService } from '../../../../controllers/ScriptService';
import { SvgCart, SvgCheck, SvgPlayCircle } from '../../../../icons';
import { AltiboxURLs } from '../../../../interfaces';
import { EpisodeAsset } from '../../../../queries/ats/titles/types/asset';
import { SvodSource } from '../../../../queries/ats/titles/types/sources';
import { getFooterUrl } from '../../../../utils/appUtils';
import { useAuth } from '../../../../utils/auth-utils';
import { getPercent } from '../../../../utils/commonUtils';
import { useDetailsImage } from '../../use-details-image';
import { EpisodeThumbnailExternalLink } from './episode-thumbnail-external-link';
import { EpisodeThumbnailLogIn } from './episode-thumbnail-log-in';
import { routes as routeUtils } from '../../../../routes/route-utils';
import styles from './episode.module.scss';

interface EpisodeThumbnailSvodProps {
  episode: EpisodeAsset;
  source: SvodSource;
}

export function EpisodeSvodThumbnail({ episode, source }: EpisodeThumbnailSvodProps) {
  const { t } = useTranslation();
  const { loggedInWithCredentials, loggedInWithIp } = useAuth();
  const isAuth = Boolean(loggedInWithCredentials || loggedInWithIp);
  const canPlay = source.isSubscribed && isAuth;
  const isWatched = source.bookmark !== undefined && source.bookmark.isWatched;
  const { isChecked: modalIsDisabledByUser } = useDontShowAgain(source.id);

  const src = useDetailsImage({ asset: episode, shape: 'wide', width: 200 });

  const image = {
    src: src,
    alt: `${episode.titleName} cover`,
  };

  const progress =
    source.bookmark !== undefined && episode.duration
      ? getPercent(source.bookmark.position, episode.duration)
      : undefined;

  if (canPlay) {
    const playLink = routeUtils.play({
      platformTitleId: source.platformTitleId,
      providerIdentifier: source.providerIdentifier,
      masterId: episode.masterId,
    });
    return (
      <Link to={playLink}>
        <Thumbnail
          image={image}
          icon={isWatched ? SvgCheck : SvgPlayCircle}
          progress={progress}
          interactions={
            isWatched
              ? {
                  backdrop: 'always',
                  icon: 'always',
                }
              : {
                  backdrop: 'hover',
                  icon: 'hover',
                }
          }
        />
      </Link>
    );
  }

  if (!isAuth) {
    return <EpisodeThumbnailLogIn image={image} />;
  }

  const serviceLink = getFooterUrl(ScriptService.isDKUser() ? AltiboxURLs.CUSTOMER_SERVICE : AltiboxURLs.MY_PAGE);
  const modalDescription = t(ScriptService.isDKUser() ? 'missing access dk message' : 'missing access message', {
    service: source.name,
  });

  // Proceed to service link
  if (modalIsDisabledByUser && !source.isSubscribed) {
    return <EpisodeThumbnailExternalLink source={source} image={image} url={serviceLink} />;
  }

  return (
    <LinkModal
      id={source.id}
      title={source.name}
      description={modalDescription}
      primaryButton={{
        title: t('go to mine sider'),
        url: serviceLink,
      }}
    >
      <button className={styles.thumbnailButton}>
        <Thumbnail image={image} icon={SvgCart} iconText={t('get access')} />
      </button>
    </LinkModal>
  );
}
